class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    // Chips, radios and the sort select apply at once; only typed prices wait for a pause. The response to a change
    // made just before (a chip ticked, then a price typed) can re-render the price group during that pause, so the
    // typed text is kept as a draft by field id until it is sent (renderFilters writes it back into the new field),
    // and the pause ends by reading the field by id: the first keystroke's event would point at a detached copy.
    // A chip, radio or sort change made during the pause sends the typed price with it. A clear or a removal (a chip in
    // the active row, Clear all, the sheet's Clear, Clear filters) and Back drop both the pause and the drafts
    // (discardPriceDrafts): the price belonged to the selection being left, and sending it after would put it back.
    this.debouncedOnSubmit = (id) => {
      clearTimeout(FacetFiltersForm.priceTimer);
      FacetFiltersForm.priceTimer = setTimeout(() => {
        FacetFiltersForm.priceTimer = null;
        FacetFiltersForm.priceDrafts = null;
        const input = document.getElementById(id);
        if (input) this.onSubmitHandler({ target: input });
      }, 800);
    };

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      if (event.target.type !== 'text') return this.onSubmitHandler(event);
      (FacetFiltersForm.priceDrafts ||= {})[event.target.id] = event.target.value;
      this.debouncedOnSubmit(event.target.id);
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) {
      facetWrapper.addEventListener('keyup', onKeyUpEscape);
      // A popover closes when Tab moves focus on to something outside it (the next trigger, Sort, the grid), so it
      // never stays open over the products behind the focus. Only a Tab stop counts: a render that replaces the
      // focused chip gives no relatedTarget, and a mouse press on a chip's label or the panel moves focus to
      // <main tabindex="-1">; both leave the popover open (the page overlay still closes it on an outside click).
      facetWrapper.addEventListener('focusout', (event) => {
        const popover = event.target.closest('details[open]');
        const next = event.relatedTarget;
        if (popover && next && next.tabIndex >= 0 && !popover.contains(next)) popover.removeAttribute('open');
      });
    }
  }

  static discardPriceDrafts() {
    clearTimeout(FacetFiltersForm.priceTimer);
    FacetFiltersForm.priceTimer = null;
    FacetFiltersForm.priceDrafts = null;
  }

  // Native controls work before this deferred script arrives. Their defaults still describe the rendered results,
  // even if a shopper has already ticked a chip, typed a price or picked a sort. Reconcile once after all forms and
  // price ranges have upgraded, so desktop filter/sort forms share one request and prices use normal validation.
  static applyInitialSelection() {
    const changedForms = [...document.querySelectorAll('facet-filters-form form')].filter((form) =>
      [...form.elements].some((input) => {
        if (!input.name || input.disabled) return false;
        if (input.type === 'checkbox' || input.type === 'radio') return input.checked !== input.defaultChecked;
        if (input.tagName === 'SELECT') {
          const initialOption = [...input.options].find((option) => option.defaultSelected) || input.options[0];
          return input.value !== initialOption?.value;
        }
        return input.type === 'text' && input.value !== input.defaultValue;
      }),
    );
    const isMobile = window.matchMedia('(max-width: 800px)').matches;
    const form = changedForms.find((form) => (form.id === 'FacetFiltersFormMobile') === isMobile) || changedForms[0];
    if (form) form.closest('facet-filters-form').onSubmitHandler({ target: form });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      FacetFiltersForm.discardPriceDrafts();
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  // Whether the desktop bar is stuck to the header (revealResults keeps it in place). Phones have no desktop bar: it is
  // never read there, so a phone tap makes no layout read at all.
  static isBarStuck() {
    if (window.matchMedia('(max-width: 800px)').matches) return false;
    const bar = document.querySelector('.at-filters');
    return bar?.offsetHeight > 0 && bar.getBoundingClientRect().top <= parseFloat(getComputedStyle(bar).top) + 0.5;
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    // Read in the next frame, when the browser lays the page out anyway: read here, in the tap, it forced a style and
    // layout pass on top of the frame's own. A render from the cache runs before that frame and reads it first itself.
    FacetFiltersForm.barWasStuck = null;
    requestAnimationFrame(() => {
      if (FacetFiltersForm.barWasStuck === null) FacetFiltersForm.barWasStuck = FacetFiltersForm.isBarStuck();
    });
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.querySelector('.facets-container')?.classList.add('is-loading');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }
    document.getElementById('SearchResultLine')?.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent, searchParams);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  // Only a complete section is cached or rendered. A failed or partial response (429, error page) would otherwise
  // replace the filters and grid with nothing, so the page loads the chosen URL instead. A response for a selection
  // the shopper has since changed is cached but not rendered; its Standard Events update still settles with that
  // selection's own count (a rejection would be logged and reported as a failed update).
  static renderSectionFromFetch(url, event, updateEvent, searchParams) {
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((html) => {
        if (!html.includes('id="ProductGridContainer"')) throw new Error('Incomplete section');
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        if (searchParams !== FacetFiltersForm.searchParamsPrev) {
          updateEvent?.resolve(parseInt(html.match(/data-product-count="(\d+)"/)?.[1]) || 0);
          return;
        }
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        updateEvent?.reject(error);
        if (searchParams !== FacetFiltersForm.searchParamsPrev) return; // a newer selection is already on its way
        window.location.assign(`${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  // Every DOM write first, then focus, then (next frame) the scroll that needs the new layout: a layout read between
  // writes made the browser restyle and lay out the page once per read (3-5 times per render).
  static renderSection(html, event, updateEvent) {
    if (FacetFiltersForm.barWasStuck === null) FacetFiltersForm.barWasStuck = FacetFiltersForm.isBarStuck();
    FacetFiltersForm.focusQueue = [];
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    FacetFiltersForm.syncSheetClear(html);
    FacetFiltersForm.focusQueue.forEach((fn) => fn());
    FacetFiltersForm.focusQueue = null;
    FacetFiltersForm.restoreFocus();
    FacetFiltersForm.syncSheetClearFocus();
    requestAnimationFrame(() => FacetFiltersForm.revealResults());
  }

  // A focus move made during a render waits until the render's writes are done (focus needs an up-to-date style).
  static focusLater(fn) {
    if (FacetFiltersForm.focusQueue) FacetFiltersForm.focusQueue.push(fn);
    else fn();
  }

  // The phone sheet's Clear lives in its footer, which renders never replace: with no filter applied (no badge on the
  // freshly rendered Filter button) it is dimmed by CSS, taken out of the Tab order and announced as unavailable.
  // Its link is taken from the new render (the section's HTML text; read with a pattern rather than parsing the whole
  // section a second time), so Clear keeps a sort order chosen since the page loaded. The pattern follows the markup
  // in snippets/facets.liquid (href first, then the class).
  static syncSheetClear(html) {
    const clear = document.querySelector('.at-sheet-clear');
    if (!clear) return;
    const href = typeof html === 'string' && html.match(/<a href="([^"]*)" class="mobile-facets__clear at-sheet-clear"/);
    if (href) {
      const decode = document.createElement('textarea');
      decode.innerHTML = href[1];
      clear.setAttribute('href', decode.value);
    }
    const none = !document.querySelector('.at-mfilters .mobile-facets__open .at-mbadge');
    if (none) {
      clear.setAttribute('aria-disabled', 'true');
      clear.setAttribute('tabindex', '-1');
    } else {
      clear.removeAttribute('aria-disabled');
      clear.removeAttribute('tabindex');
    }
  }

  // Clear pressed from the keyboard with nothing left to clear: focus moves on to Show rather than resting on a control
  // that no longer does anything.
  static syncSheetClearFocus() {
    const clear = document.querySelector('.at-sheet-clear');
    if (clear && document.activeElement === clear && clear.getAttribute('aria-disabled') === 'true') {
      document.querySelector('.at-sheet-apply')?.focus();
    }
  }

  // After a chip, Clear all or Clear filters was pressed from the keyboard: the chip that took the removed one's place
  // in the same row (the desktop row under the bar, or the phone bar's chip row), else Clear all (the removed chip was
  // the last), else the first filter trigger (desktop) or Filter (phones).
  static restoreFocus() {
    const after = FacetFiltersForm.focusAfter;
    FacetFiltersForm.focusAfter = null;
    if (!after || (document.activeElement && document.activeElement !== document.body)) return;
    const row = document.querySelector(after.row || '.at-factive');
    const chips = row ? [...row.querySelectorAll('a.at-fchip')] : [];
    let target = after.chip >= 0 ? chips[after.chip] : null;
    if (!target && chips.length) target = row.querySelector('.at-fclear');
    if (!target) {
      const bar = document.querySelector('.at-filters');
      target = bar?.offsetHeight
        ? bar.querySelector('.at-ftriggers summary, .at-ftriggers input')
        : document.querySelector('.at-mfilters .mobile-facets__open-wrapper');
    }
    target?.focus({ preventScroll: true });
  }

  // A change made while the desktop bar is stuck (the bar invites filtering from deep in the grid) keeps the bar, and
  // its open popover, exactly where they are and brings the new results up under it: the element under the bar
  // (.at-factive, the active chips, a zero-height box when empty) is moved to the bar's bottom edge. That row is sticky
  // too (pinned under the stuck bar), so its place in the flow is read from the results section after it. This also covers a
  // shorter result set, which would otherwise leave the shopper in the footer, and the active row appearing or
  // collapsing (Chrome's scroll anchoring would otherwise shift the bar off its stuck position). From above the bar
  // nothing moves.
  // Phones (the desktop bar has no height): the grid returns to its scroll-margin under the phone bar, behind the sheet,
  // so closing the sheet by any means (Show, X, the backdrop) lands on the results.
  static revealResults() {
    const bar = document.querySelector('.at-filters');
    if (bar?.offsetHeight) {
      if (!FacetFiltersForm.barWasStuck) return;
      const below = bar.parentElement.querySelector('.at-factive');
      const next = document.getElementById('ProductGridContainer').closest('.at-coll');
      const flowTop = next ? next.getBoundingClientRect().top - below.offsetHeight : below.getBoundingClientRect().top;
      const shift = flowTop - parseFloat(getComputedStyle(bar).top) - bar.offsetHeight;
      if (Math.abs(shift) >= 1) window.scrollBy({ top: shift, behavior: 'instant' });
      return;
    }
    // Also when the grid's top is only partly under the stuck header and phone bar (a shopper one row in), whose first
    // photos would otherwise stay cut off behind them; the bar's bottom stops counting once the bar has scrolled away.
    const grid = document.getElementById('ProductGridContainer');
    const phoneBar = document.querySelector('.at-mfilters');
    const hiddenAbove = Math.max(0, phoneBar ? phoneBar.getBoundingClientRect().bottom : 0);
    if (grid.getBoundingClientRect().top < hiddenAbove) grid.scrollIntoView({ behavior: 'instant' });
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    // The new cards carry empty HelpfulCrowd rating slots; ask the store script to fill them (no-op if it is absent).
    if (typeof window.atRefreshReviews === 'function') window.atRefreshReviews();
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    // Atelier: the open phone sheet is modal and the bar's count behind it inert, so the sheet says it (facets.liquid).
    const sheetCount = document.querySelector('[data-at-sheet-count]');
    if (sheetCount) sheetCount.textContent = container.textContent.trim();
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    const facets = container.closest('.facets-container');
    facets?.classList.toggle('is-empty', container.dataset.productCount === '0');
    facets?.classList.remove('is-loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    // Atelier search: the result line under the H1 (main-search.liquid) sits outside #ProductGridContainer and the
    // facets, so it would go stale after a filter change; refresh it from the fetched section with the count.
    const sourceLine = parsedHtml.getElementById('SearchResultLine');
    const line = document.getElementById('SearchResultLine');
    if (sourceLine && line) {
      line.innerHTML = sourceLine.innerHTML;
      line.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    // A price field being typed in when its group is re-rendered gets focus and the caret back (below).
    const active = document.activeElement;
    const typing =
      active?.type === 'text' && active.closest('.js-filter')
        ? { id: active.id, start: active.selectionStart, end: active.selectionEnd }
        : null;
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            FacetFiltersForm.focusLater(() => matchingInput.focus());
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) FacetFiltersForm.focusLater(() => fallbackElement.focus());
          }
        }
      }
    }

    const drafts = FacetFiltersForm.priceDrafts;
    if (drafts) {
      Object.keys(drafts).forEach((id) => {
        const input = document.getElementById(id);
        if (input) input.value = drafts[id];
      });
    }
    const field = typing && document.getElementById(typing.id);
    if (field) {
      FacetFiltersForm.focusLater(() => {
        if (document.activeElement === field) return;
        field.focus({ preventScroll: true });
        field.setSelectionRange(typing.start, typing.end);
      });
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const row = document.querySelector(selector);
      if (!activeFacetsElement || !row) return;
      // A row that was empty (no filter on) and now has chips eases in once (at-collection.css .at-in; nothing moves
      // for reduced motion); a row that changes while it has chips just updates.
      const appearing = !row.firstElementChild && activeFacetsElement.firstElementChild;
      row.innerHTML = activeFacetsElement.innerHTML;
      if (appearing) {
        row.classList.add('at-in');
        row.addEventListener('animationend', () => row.classList.remove('at-in'), { once: true });
      }
      // The chip rows print nothing with no filter applied (at-collection.css .has-active: the hairline under the desktop
      // bar, the grid's scroll margin under the taller phone bar).
      if (selector === '.active-facets-mobile') {
        row.closest('.facets-container')?.classList.toggle('has-active', row.firstChild !== null);
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '.at-msort-label'];
    // The desktop Sort select is replaced too; a keyboard shopper who just changed it keeps focus on the new one.
    const sortHadFocus = Boolean(document.activeElement?.closest('.sorting'));

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });
    if (sortHadFocus) FacetFiltersForm.focusLater(() => document.querySelector('.sorting select')?.focus());

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    // .at-fsum: the Atelier popover trigger, whose count badge follows the ticks made inside it.
    const targetSummary = target.querySelector('.facets__summary, .at-fsum');
    const sourceSummary = source.querySelector('.facets__summary, .at-fsum');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  // Clean URLs: no empty fields (the price inputs) and no sort_by when it is the default sort.
  createSearchParams(form) {
    form.querySelectorAll('price-range').forEach((range) => range.validate());
    const searchParams = new URLSearchParams();
    for (const [name, value] of new FormData(form)) {
      if (value !== '') searchParams.append(name, value);
    }
    if (searchParams.get('sort_by') === form.dataset.defaultSort) searchParams.delete('sort_by');
    return searchParams.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  // event: the input event, or { target } for a typed price once its pause is over (see the constructor).
  onSubmitHandler(event) {
    event.preventDefault?.();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.target.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.discardPriceDrafts();
    FacetFiltersForm.toggleActiveFacets();
    // The pressed link (a chip, Clear all, the empty state's Clear filters) is replaced by the re-render, which would
    // drop keyboard focus to <body>; remember where it was so restoreFocus can put it back nearby.
    const link = event.currentTarget;
    const row = link.closest('.at-factive, .at-mactive');
    if (row) link.classList.add('is-removing'); // the chip steps back at once; the re-render replaces it
    if (link.contains(document.activeElement)) {
      FacetFiltersForm.focusAfter = {
        chip: row ? [...row.querySelectorAll('a.at-fchip')].indexOf(link) : -1,
        row: row?.classList.contains('at-mactive') ? '.at-mactive' : '.at-factive',
      };
    }
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('input', () => {
        this.lastInput = element;
      });
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  // Validate when the form is serialized, after the typing pause (or a chip/sort change). Blur used to clamp only
  // the field, after the request had already sent the impossible range, leaving the URL and results behind.
  validate() {
    this.querySelectorAll('input').forEach((input) => this.normalize(input)); // both, before either is compared with the other
    this.setMinAndMaxValues();
    if (this.lastInput) this.adjustToValidValues(this.lastInput);
    this.setMinAndMaxValues();
    this.querySelectorAll('input').forEach((input) => {
      this.adjustToValidValues(input);
      if (FacetFiltersForm.priceDrafts?.[input.id] !== undefined) {
        FacetFiltersForm.priceDrafts[input.id] = input.value;
      }
    });
    this.setMinAndMaxValues();
  }

  // Only stray printable characters are refused. Shortcuts (Ctrl on Windows and Linux, Cmd on a Mac), Home/End and
  // other named keys, and IME composition all pass; adjustToValidValues cleans whatever arrives by paste.
  onKeyDown(event) {
    if (event.metaKey || event.ctrlKey || event.altKey || event.isComposing) return;
    if (event.key.length === 1 && !/[0-9.,' ]/.test(event.key)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  // Grouping marks ("1,000", "1 000", "1'000") are dropped; anything still not a number, or below zero, means no limit
  // rather than a NaN that no clamp catches and that would go into the filter link as typed.
  normalize(input) {
    if (input.value.trim() === '') return;
    const cleaned = input.value.replace(/[,'\s]/g, '');
    const value = Number(cleaned);
    if (!Number.isFinite(value) || value < 0) input.value = '';
    else if (cleaned !== input.value) input.value = cleaned;
  }

  adjustToValidValues(input) {
    this.normalize(input);
    if (input.value.trim() === '') return; // an emptied field means no limit (Number('') is 0, which clamped it to the other end)
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

FacetFiltersForm.applyInitialSelection();

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    // No filter form on the page (a section with Enable filtering and Enable sorting both off): the link does what a
    // plain link does. Space on it (role=button) clicks it, and that click navigates.
    if (!form) {
      if (event.type === 'keyup') event.currentTarget.click();
      return;
    }
    event.preventDefault();
    if (event.currentTarget.getAttribute('aria-disabled') === 'true') return; // the sheet's Clear with nothing to clear
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

// Phones: Sort opens the filter sheet in its sort view (facets.liquid), and Dawn's MenuDrawer puts focus on the Filter
// button behind the backdrop both when the sheet opens and when it closes. A keyboard or screen-reader shopper who
// pressed Sort lands on the order in use instead, and comes back to Sort when the sheet closes (a pick, the X, Escape
// or the backdrop). Filter keeps Dawn's behaviour. The Sort button's inline onclick has run (sheet open, sort view)
// by the time this click listener sees the event.
document.addEventListener('click', (event) => {
  const sort = event.target.closest?.('.at-msort');
  if (!sort) return;
  sort.closest('.facets-container')?.querySelector('.at-sheet-sort input:checked')?.focus({ preventScroll: true });
});
document.addEventListener('focusin', (event) => {
  const summary = event.target.closest?.('.at-mfilters .mobile-facets__open-wrapper');
  if (!summary) return;
  const details = summary.parentElement;
  const container = summary.closest('.facets-container');
  // Closing: MenuDrawer drops .menu-opening and focuses the summary while [open] stays for the close animation.
  // Opening: the summary is focused before [open] is set, so this does not fire then.
  if (details.open && !details.classList.contains('menu-opening') && container?.dataset.view === 'sort') {
    container.querySelector('.at-msort')?.focus();
  }
});
// Enable filtering off: the phone bar's summary is the Sort button (facets.liquid, no .at-msort), so the same hand-off
// happens here once the sheet is open: focus on the order in use. Closing brings focus back to the summary (Dawn).
document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (!details.open || !details.matches?.('.at-mfilters.at-mone .mobile-facets__disclosure')) return;
    if (details.closest('.facets-container')?.dataset.view !== 'sort') return;
    details.querySelector('.at-sheet-sort input:checked')?.focus({ preventScroll: true });
  },
  true,
);

// Desktop Sort pill: Chromium and WebKit match :focus-visible on a <select> opened with the mouse, so its keyboard ring
// (at-collection.css) keys on how the shopper last moved: a key press sets html.at-kbd, a pointer press clears it.
document.addEventListener('keydown', (event) => {
  if (!event.metaKey && !event.ctrlKey) document.documentElement.classList.add('at-kbd');
}, true);
document.addEventListener('pointerdown', () => document.documentElement.classList.remove('at-kbd'), true);

// Phone sheet, keyboard: Tab and Shift+Tab stay inside the open sheet and skip what the current view hides. Dawn's
// trapFocus picks its first and last elements once, when the sheet opens, from everything in it, so in the sort view
// (or after a re-render) its "last" was a hidden footer control and Tab escaped to the page behind the backdrop.
// Radios count once per group (the chosen one), as the browser's own Tab does.
window.addEventListener('keydown', (event) => {
  if (event.key !== 'Tab' || event.ctrlKey || event.metaKey) return;
  const details = document.querySelector('.at-mfilters .mobile-facets__disclosure[open].menu-opening');
  const sheet = details?.querySelector('.mobile-facets__inner');
  if (!sheet) return;
  const seen = new Set();
  const items = [...sheet.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')].filter((el) => {
    if (el.disabled || el.tabIndex < 0 || !el.getClientRects().length || getComputedStyle(el).visibility === 'hidden') return false;
    if (el.type !== 'radio') return true;
    if (seen.has(el.name)) return false;
    const group = [...sheet.querySelectorAll(`input[type=radio][name="${CSS.escape(el.name)}"]`)];
    if ((group.find((r) => r.checked) || group[0]) !== el) return false;
    seen.add(el.name);
    return true;
  });
  if (!items.length) return;
  const active = document.activeElement;
  let index = items.indexOf(active);
  if (index < 0 && active?.type === 'radio') index = items.findIndex((el) => el.type === 'radio' && el.name === active.name);
  const next = index < 0
    ? items[event.shiftKey ? items.length - 1 : 0]
    : items[(index + (event.shiftKey ? -1 : 1) + items.length) % items.length];
  event.preventDefault();
  event.stopImmediatePropagation();
  next.focus();
}, true);

// Phone sheet opened by Sort and closed by a tap on the dimmed backdrop (the summary's overlay): the tap focuses the
// Filter summary before the sheet starts closing, so the focusin hand-off above never sees it. Once Dawn's click handler
// has started the close (menu-opening gone, [open] still set for the animation), focus goes back to Sort.
document.addEventListener('click', (event) => {
  const summary = event.target.closest?.('.at-mfilters .mobile-facets__open-wrapper');
  if (!summary) return;
  const details = summary.parentElement;
  const container = summary.closest('.facets-container');
  if (details.open && !details.classList.contains('menu-opening') && container?.dataset.view === 'sort') {
    container.querySelector('.at-msort')?.focus();
  }
});
